import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BiArrowBack, BiQr, BiCheckCircle, BiErrorCircle } from "react-icons/bi";
import { Colors } from "../../../assets/Colors";

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return { Authorization: "Bearer " + user.Token };
};

// Halaman petugas jaga prasmanan: input kode makan penghuni, rekap harian, riwayat + pembatalan.
export default function PrasmananPage() {
  const navigate = useNavigate();
  const [kode, setKode] = useState("");
  const [hasil, setHasil] = useState(null); // { ok, teks, data }
  const [rekap, setRekap] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const muat = useCallback(() => {
    axios.get(API + "getRekapMakan", { headers: authHeaders() })
      .then((r) => setRekap(r.data.data))
      .catch((e) => toast.error(e.response?.data?.message || "Gagal memuat rekap"));
  }, []);
  useEffect(() => { muat(); }, [muat]);

  const kirim = async (e) => {
    e && e.preventDefault();
    if (!kode.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(API + "pakaiMakanKode", { Kode: kode.trim() }, { headers: authHeaders() });
      setHasil({ ok: true, data: r.data.data });
      setKode("");
      muat();
    } catch (err) {
      setHasil({ ok: false, teks: err.response?.data?.message || "Gagal memproses kode" });
    } finally {
      setBusy(false);
      inputRef.current && inputRef.current.focus();
    }
  };

  const batal = async (l) => {
    const alasan = window.prompt(`Batalkan pemakaian ${l.Nama} (${l.WaktuNama})?\nTulis alasan pembatalan:`);
    if (!alasan || !alasan.trim()) return;
    try {
      await axios.post(API + "batalMakanKode", { KodeID: l.KodeID, Alasan: alasan.trim() }, { headers: authHeaders() });
      toast.success("Pemakaian dibatalkan, hak makan dikembalikan");
      setHasil(null);
      muat();
    } catch (err) { toast.error(err.response?.data?.message || "Gagal membatalkan"); }
  };

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">
      <div className="flex items-center justify-between py-4">
        <button onClick={() => navigate("/general-affair")} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/general-affair/prasmanan/barcode")}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm text-gray-700 hover:bg-gray-50">
            <BiQr /> Cetak Barcode Kamar
          </button>
          <span className="text-sm font-semibold" style={{ color: Colors.primary }}>Prasmanan — Petugas Jaga</span>
        </div>
      </div>

      <form onSubmit={kirim} className="bg-white rounded-xl border p-5 mb-4">
        <label className="block text-xs font-bold uppercase text-gray-600 mb-1">Kode makan dari penghuni</label>
        <div className="flex gap-2">
          <input ref={inputRef} autoFocus value={kode} onChange={(e) => setKode(e.target.value.toUpperCase())}
            maxLength={8} placeholder="mis. VFD93F" autoComplete="off"
            className="flex-1 border-2 rounded-lg px-4 py-3 text-2xl font-mono tracking-widest uppercase outline-none focus:border-orange-400" />
          <button type="submit" disabled={busy || !kode.trim()}
            className="px-6 rounded-lg text-white font-semibold disabled:opacity-50" style={{ background: Colors.primary }}>
            {busy ? "…" : "Konfirmasi"}
          </button>
        </div>

        {hasil && hasil.ok && (
          <div className="mt-4 rounded-lg border-2 border-green-400 bg-green-50 p-4 flex items-start gap-3">
            <BiCheckCircle className="text-3xl text-green-600 shrink-0" />
            <div>
              <div className="font-bold text-green-800">Kode valid — silakan beri makan</div>
              <div className="text-lg font-semibold">{hasil.data.Nama}</div>
              <div className="text-sm text-gray-700">
                {hasil.data.Jenis === "KLIEN" ? `Klien${hasil.data.Klien ? " · " + hasil.data.Klien : ""}` : "Karyawan"}
                {" · "}Kamar {hasil.data.Kamar}{" · "}{hasil.data.WaktuNama}{" · "}pukul {hasil.data.Waktu}
              </div>
            </div>
          </div>
        )}
        {hasil && !hasil.ok && (
          <div className="mt-4 rounded-lg border-2 border-red-400 bg-red-50 p-4 flex items-start gap-3">
            <BiErrorCircle className="text-3xl text-red-600 shrink-0" />
            <div>
              <div className="font-bold text-red-800">Ditolak</div>
              <div className="text-sm text-gray-800">{hasil.teks}</div>
            </div>
          </div>
        )}
      </form>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {(rekap?.Waktu || []).map((w) => (
          <div key={w.WaktuID} className="bg-white rounded-xl border p-4">
            <div className="text-xs font-bold uppercase text-gray-500">{w.Nama}</div>
            <div className="text-3xl font-bold" style={{ color: Colors.primary }}>{w.Total}</div>
            <div className="text-xs text-gray-500">Karyawan {w.Karyawan} · Klien {w.Klien}</div>
            <div className="text-[11px] text-gray-400">Kode diterbitkan: {w.Diterbitkan}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-5 py-3 border-b font-semibold text-sm" style={{ background: Colors.primary + "15", color: Colors.primary }}>
          Riwayat hari ini ({rekap?.Tanggal})
        </div>
        {!rekap || rekap.Log.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">Belum ada pengambilan makan hari ini.</div>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-xs text-gray-600">
                {["Jam", "Waktu", "Penghuni", "Jenis / Klien", "Kamar", "Petugas", "Kode", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold border border-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rekap.Log.map((l) => (
                <tr key={l.KodeID} className="hover:bg-orange-50">
                  <td className="px-3 py-1.5 border border-gray-300">{l.Waktu}</td>
                  <td className="px-3 py-1.5 border border-gray-300">{l.WaktuNama}</td>
                  <td className="px-3 py-1.5 border border-gray-300 font-medium">{l.Nama}</td>
                  <td className="px-3 py-1.5 border border-gray-300">{l.Jenis === "KLIEN" ? `Klien ${l.Klien}` : "Karyawan"}</td>
                  <td className="px-3 py-1.5 border border-gray-300">{l.Kamar}</td>
                  <td className="px-3 py-1.5 border border-gray-300">{l.Petugas}</td>
                  <td className="px-3 py-1.5 border border-gray-300 font-mono text-xs">{l.Kode}</td>
                  <td className="px-3 py-1.5 border border-gray-300 text-center">
                    <button onClick={() => batal(l)} className="text-xs text-red-500 hover:text-red-700 underline">Batalkan</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
